import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Copy,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { Dialog } from '../reusable';
import { apiClient, API_BASE_URL } from '../../services/apiClient';
import { useUIColors } from '../../hooks/useTheme';

const isActiveFlag = (value) => {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return Number(value) !== 0;
};

const STATUS_STYLES = {
  Activo: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
  Inactivo: { bg: '#e5e7eb', text: '#374151', border: '#d1d5db' },
  default: { bg: '#e5e7eb', text: '#374151', border: '#d1d5db' },
};

const formatMoney = (value) => {
  const n = Number(value || 0);
  return `$ ${n.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
};

const renderStatusBadge = (value) => {
  const status = String(value || 'Activo').trim() || 'Activo';
  const palette = STATUS_STYLES[status] || STATUS_STYLES.default;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '64px',
        padding: '2px 7px',
        borderRadius: '999px',
        fontSize: '10px',
        fontWeight: 700,
        backgroundColor: palette.bg,
        color: palette.text,
        border: `1px solid ${palette.border}`,
      }}
    >
      {status}
    </span>
  );
};

const TABLE_COLUMNS = [
  { key: 'SKU', align: 'left', sortKey: 'upselling_code' },
  { key: 'Producto', align: 'left', sortKey: 'product_name' },
  { key: 'Formato', align: 'left', sortKey: 'format_code' },
  { key: 'Marca', align: 'left', sortKey: 'brand_code' },
  { key: 'Precio', align: 'right', sortKey: 'quoted_price' },
  { key: 'Cotizado', align: 'right', sortKey: 'quoted_count' },
  { key: 'Cantidad', align: 'right', sortKey: 'quoted_quantity' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
  { key: 'Acciones', align: 'right', sortKey: null },
];

/** Indentación jerárquica (px, sumados a padH). */
const HIERARCHY_INDENT_PRODUCT = 28;
const HIERARCHY_ITEM_SKU_INDENT = 64;
const HIERARCHY_ITEM_PRODUCT_INDENT = 12;

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'upselling_code':
      return String(row.upselling_code || '').toLowerCase();
    case 'category_name':
      return String(row.category_name || '').toLowerCase();
    case 'product_name':
      return String(row.product_name || '').toLowerCase();
    case 'format_code':
      return String(row.format_code || '').toLowerCase();
    case 'brand_code':
      return String(row.brand_code || '').toLowerCase();
    case 'base_price':
      return Number(row.base_price) || 0;
    case 'quoted_price':
      return Number(row.min_quoted_price ?? row.base_price) || 0;
    case 'quoted_count':
      return Number(row.quoted_count) || 0;
    case 'quoted_quantity':
      return Number(row.quoted_quantity) || 0;
    case 'sold_quantity':
      return Number(row.sold_quantity) || 0;
    case 'product_type_code':
      return `${row.product_type_code || ''}${row.format_code || ''}${row.brand_code || ''}`.toLowerCase();
    case 'spec_digits':
      return Number(row.spec_digits) || 0;
    case 'status':
      return String(row.status || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') {
    return (va - vb) * mult;
  }
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

const productTypeKey = (categoryCode, productTypeCode) =>
  `${String(categoryCode || '')}|${String(productTypeCode || '').padStart(2, '0')}`;

/**
 * Tabla de productos upselling (A012): mismo look que Table3 / SalesTable3 / QuotationsTable3.
 */
const UpsellingTable3 = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  void isDarkMode;
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 4;
  const padH = 10;
  const thPadV = 5;

  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  /** Categorías raíz (UP##) contraídas en la tabla de productos. */
  const [collapsedProductCategories, setCollapsedProductCategories] = useState(() => new Set());
  /** Tipos de producto (UP##|NN) contraídos dentro de cada categoría. */
  const [collapsedProductTypes, setCollapsedProductTypes] = useState(() => new Set());
  const [productHierarchyInitialized, setProductHierarchyInitialized] = useState(false);
  const [sortColumn, setSortColumn] = useState('upselling_code');
  const [sortDir, setSortDir] = useState('desc');

  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productSpecLabel, setProductSpecLabel] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [productName, setProductName] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [categoryMode, setCategoryMode] = useState('auto');
  const [manualCategoryCode, setManualCategoryCode] = useState('');
  const [manualProductTypeCode, setManualProductTypeCode] = useState('');
  const [manualFormatCode, setManualFormatCode] = useState('CA');
  const [manualBrandCode, setManualBrandCode] = useState('MXX');
  const [brands, setBrands] = useState([]);
  const [newCatCode, setNewCatCode] = useState('');
  const [newCatName, setNewCatName] = useState('');
  const [saveProductLoading, setSaveProductLoading] = useState(false);
  const [saveCategoryLoading, setSaveCategoryLoading] = useState(false);
  const [productFormError, setProductFormError] = useState('');
  const [categoryFormError, setCategoryFormError] = useState('');
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editSortOrder, setEditSortOrder] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [saveEditCategoryLoading, setSaveEditCategoryLoading] = useState(false);
  const [editCategoryFormError, setEditCategoryFormError] = useState('');
  const [workspaceTab, setWorkspaceTab] = useState('products');
  const [newCategoryParentId, setNewCategoryParentId] = useState('');
  const [editCategoryCode, setEditCategoryCode] = useState('');
  const [editParentId, setEditParentId] = useState('');
  /** IDs de categorías raíz cuyas subcategorías están ocultas en la tabla. */
  const [collapsedRootIds, setCollapsedRootIds] = useState(() => new Set());

  const [formats, setFormats] = useState([]);
  const [formatsLoading, setFormatsLoading] = useState(false);
  const [formatsSyncing, setFormatsSyncing] = useState(false);
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [newFormatCode, setNewFormatCode] = useState('');
  const [newFormatName, setNewFormatName] = useState('');
  const [saveFormatLoading, setSaveFormatLoading] = useState(false);
  const [formatFormError, setFormatFormError] = useState('');
  const [showEditFormatModal, setShowEditFormatModal] = useState(false);
  const [editFormatId, setEditFormatId] = useState(null);
  const [editFormatCode, setEditFormatCode] = useState('');
  const [editFormatName, setEditFormatName] = useState('');
  const [editFormatSortOrder, setEditFormatSortOrder] = useState('');
  const [editFormatIsActive, setEditFormatIsActive] = useState(true);
  const [saveEditFormatLoading, setSaveEditFormatLoading] = useState(false);
  const [editFormatFormError, setEditFormatFormError] = useState('');

  const [brandsLoading, setBrandsLoading] = useState(false);
  const [showBrandModal, setShowBrandModal] = useState(false);
  const [newBrandCode, setNewBrandCode] = useState('M');
  const [newBrandName, setNewBrandName] = useState('');
  const [saveBrandLoading, setSaveBrandLoading] = useState(false);
  const [brandFormError, setBrandFormError] = useState('');
  const [showEditBrandModal, setShowEditBrandModal] = useState(false);
  const [editBrandId, setEditBrandId] = useState(null);
  const [editBrandCode, setEditBrandCode] = useState('');
  const [editBrandName, setEditBrandName] = useState('');
  const [editBrandSortOrder, setEditBrandSortOrder] = useState('');
  const [editBrandIsActive, setEditBrandIsActive] = useState(true);
  const [saveEditBrandLoading, setSaveEditBrandLoading] = useState(false);
  const [editBrandFormError, setEditBrandFormError] = useState('');

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '160px',
    outline: 'none',
  };

  const loadCategories = useCallback(async () => {
    try {
      const data = await apiClient(
        `${API_BASE_URL}/api/quotations/upselling-products/categories?include_inactive=true`
      );
      setCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setCategories([]);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient(`${API_BASE_URL}/api/quotations/upselling-products`, {
        timeoutMs: 60000,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setRows([]);
      setError(e?.message || 'No se pudieron cargar los productos de upselling.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFormats = useCallback(async () => {
    setFormatsLoading(true);
    try {
      const data = await apiClient(
        `${API_BASE_URL}/api/quotations/upselling-products/formats?include_inactive=true`
      );
      setFormats(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setFormats([]);
    } finally {
      setFormatsLoading(false);
    }
  }, []);

  const loadBrands = useCallback(async () => {
    setBrandsLoading(true);
    try {
      const data = await apiClient(
        `${API_BASE_URL}/api/quotations/upselling-products/brands?include_inactive=true`
      );
      setBrands(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setBrands([]);
    } finally {
      setBrandsLoading(false);
    }
  }, []);

  const syncFormatsFromPaint = useCallback(async ({ silent = false } = {}) => {
    setFormatsSyncing(true);
    try {
      await apiClient(`${API_BASE_URL}/api/quotations/upselling-products/formats/sync-from-paint`, {
        method: 'POST',
      });
      await loadFormats();
    } catch (e) {
      console.error(e);
      if (!silent) {
        window.alert(e?.message || 'No se pudo sincronizar formatos desde Pinturas.');
      }
    } finally {
      setFormatsSyncing(false);
    }
  }, [loadFormats]);

  useEffect(() => {
    loadCategories();
    loadProducts();
    loadFormats();
    loadBrands();
  }, [loadCategories, loadProducts, loadFormats, loadBrands]);

  const formatsSyncedRef = useRef(false);
  useEffect(() => {
    if (workspaceTab !== 'formats' || formatsSyncedRef.current) return;
    formatsSyncedRef.current = true;
    void syncFormatsFromPaint({ silent: true });
  }, [workspaceTab, syncFormatsFromPaint]);

  const filteredRows = useMemo(() => {
    let list = rows;
    const q = searchQuery.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.upselling_code,
          row.legacy_code,
          row.category_name,
          row.category_code,
          row.product_type_code,
          row.format_code,
          row.brand_code,
          row.spec_label,
          row.product_name,
          row.base_price,
          row.spec_digits,
          row.category_corr,
          row.status,
        ]
          .filter((x) => x !== undefined && x !== null)
          .map((x) => String(x))
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterCategory) {
      list = list.filter((row) => String(row.category_code || '') === filterCategory);
    }
    return list;
  }, [rows, searchQuery, filterCategory]);

  useEffect(() => {
    if (productHierarchyInitialized || rows.length === 0) return;
    const typeKeys = new Set();
    rows.forEach((row) => {
      typeKeys.add(productTypeKey(row.category_code, row.product_type_code));
    });
    setCollapsedProductTypes(typeKeys);
    setProductHierarchyInitialized(true);
  }, [rows, productHierarchyInitialized]);

  const activeCategoriesForSelect = useMemo(
    () => categories.filter((c) => isActiveFlag(c.is_active) && !c.parent_id),
    [categories],
  );

  const productTypeMeta = useMemo(() => {
    const roots = categories.filter((c) => !c.parent_id);
    const meta = {};
    categories.forEach((c) => {
      if (c.parent_id == null) return;
      const root = roots.find((r) => r.id === c.parent_id);
      if (!root) return;
      meta[productTypeKey(root.code, c.code)] = {
        name: c.name,
        sortOrder: Number(c.sort_order) || 0,
      };
    });
    return meta;
  }, [categories]);

  const rootCategoryOrder = useMemo(
    () =>
      categories
        .filter((c) => !c.parent_id)
        .sort(
          (a, b) =>
            (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0) ||
            String(a.code || '').localeCompare(String(b.code || ''), 'es')
        )
        .map((c) => String(c.code || '')),
    [categories]
  );

  const productHierarchy = useMemo(() => {
    const rootNames = Object.fromEntries(
      categories.filter((c) => !c.parent_id).map((c) => [String(c.code || ''), c.name])
    );
    const byCat = new Map();

    filteredRows.forEach((row) => {
      const catCode = String(row.category_code || 'UP13');
      if (!byCat.has(catCode)) {
        byCat.set(catCode, {
          code: catCode,
          name: row.category_name || rootNames[catCode] || catCode,
          types: new Map(),
        });
      }
      const cat = byCat.get(catCode);
      const tKey = productTypeKey(catCode, row.product_type_code);
      if (!cat.types.has(tKey)) {
        const typeMeta = productTypeMeta[tKey];
        const nameFromRow = String(row.product_name || '').split(' - ')[0].trim();
        cat.types.set(tKey, {
          key: tKey,
          code: String(row.product_type_code || '').padStart(2, '0'),
          name: typeMeta?.name || nameFromRow || `Producto ${row.product_type_code || '—'}`,
          sortOrder: typeMeta?.sortOrder ?? 999,
          items: [],
        });
      }
      cat.types.get(tKey).items.push(row);
    });

    const compareCatCodes = (a, b) => {
      const ia = rootCategoryOrder.indexOf(a);
      const ib = rootCategoryOrder.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b, 'es');
    };

    return Array.from(byCat.values())
      .sort((a, b) => compareCatCodes(a.code, b.code))
      .map((cat) => {
        const types = Array.from(cat.types.values()).sort((a, b) => {
          const na = Number.parseInt(a.code, 10);
          const nb = Number.parseInt(b.code, 10);
          if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
          return (
            String(a.code).localeCompare(String(b.code), 'es', { numeric: true }) ||
            a.name.localeCompare(b.name, 'es')
          );
        });
        types.forEach((type) => {
          type.items.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
        });
        const itemCount = types.reduce((sum, type) => sum + type.items.length, 0);
        return { ...cat, types, itemCount, typeCount: types.length };
      });
  }, [filteredRows, categories, productTypeMeta, rootCategoryOrder, sortColumn, sortDir]);

  const autoExpandProductHierarchy = Boolean(searchQuery.trim()) || Boolean(filterCategory);

  const allProductCategoryCodes = useMemo(
    () => productHierarchy.map((group) => group.code),
    [productHierarchy]
  );

  const allProductTypeKeys = useMemo(() => {
    const keys = new Set();
    productHierarchy.forEach((group) => {
      group.types.forEach((type) => keys.add(type.key));
    });
    return keys;
  }, [productHierarchy]);

  const displayProductRows = useMemo(() => {
    const out = [];
    productHierarchy.forEach((catGroup) => {
      const catCollapsed = !autoExpandProductHierarchy && collapsedProductCategories.has(catGroup.code);
      out.push({
        kind: 'category',
        key: `cat-${catGroup.code}`,
        ...catGroup,
        collapsed: catCollapsed,
      });
      if (catCollapsed) return;

      catGroup.types.forEach((typeGroup) => {
        const typeCollapsed = !autoExpandProductHierarchy && collapsedProductTypes.has(typeGroup.key);
        out.push({
          kind: 'productType',
          key: `type-${typeGroup.key}`,
          categoryCode: catGroup.code,
          categoryName: catGroup.name,
          ...typeGroup,
          collapsed: typeCollapsed,
        });
        if (typeCollapsed) return;
        typeGroup.items.forEach((row) => {
          out.push({ kind: 'item', key: `item-${row.id}`, row });
        });
      });
    });
    return out;
  }, [
    productHierarchy,
    autoExpandProductHierarchy,
    collapsedProductCategories,
    collapsedProductTypes,
  ]);

  const toggleProductCategoryCollapsed = (catCode) => {
    setCollapsedProductCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catCode)) next.delete(catCode);
      else next.add(catCode);
      return next;
    });
  };

  const toggleProductTypeCollapsed = (typeKey) => {
    setCollapsedProductTypes((prev) => {
      const next = new Set(prev);
      if (next.has(typeKey)) next.delete(typeKey);
      else next.add(typeKey);
      return next;
    });
  };

  const expandAllProductCategories = () => setCollapsedProductCategories(new Set());

  const collapseAllProductCategories = () => {
    setCollapsedProductCategories(new Set(allProductCategoryCodes));
  };

  const expandAllProductTypes = () => setCollapsedProductTypes(new Set());

  const collapseAllProductTypes = () => {
    setCollapsedProductTypes(new Set(allProductTypeKeys));
  };

  /** Categorías raíz activas (elegir padre al crear subcategoría / código de producto). */
  const rootCategories = useMemo(
    () =>
      categories.filter(
        (c) => c.id != null && !c.parent_id && isActiveFlag(c.is_active)
      ),
    [categories],
  );

  const productTypesForManualCategory = useMemo(() => {
    const parent = categories.find(
      (c) => !c.parent_id && String(c.code) === String(manualCategoryCode)
    );
    if (!parent?.id) return [];
    return categories
      .filter((c) => c.parent_id === parent.id && isActiveFlag(c.is_active))
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  }, [categories, manualCategoryCode]);

  const activeFormatsForSelect = useMemo(
    () => formats.filter((f) => isActiveFlag(f.is_active)),
    [formats],
  );

  const activeBrandsForSelect = useMemo(
    () => brands.filter((b) => isActiveFlag(b.is_active)),
    [brands],
  );

  const rootCategoriesForParentSelect = useMemo(
    () => categories.filter((c) => c.id != null && !c.parent_id && c.id !== editCategoryId),
    [categories, editCategoryId],
  );

  const editCategoryHasChildren = useMemo(
    () => editCategoryId != null && categories.some((x) => x.parent_id === editCategoryId),
    [editCategoryId, categories],
  );

  /** Orden visual: raíz y debajo sus subcategorías (lista completa antes de filtrar por contraído). */
  const categoryRowsFull = useMemo(() => {
    const flat = categories.filter((c) => c.id != null);
    if (!flat.length) return [];
    const roots = flat
      .filter((c) => !c.parent_id)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const byParent = {};
    flat.forEach((c) => {
      if (!c.parent_id) return;
      const k = c.parent_id;
      if (!byParent[k]) byParent[k] = [];
      byParent[k].push(c);
    });
    Object.keys(byParent).forEach((k) => {
      byParent[k].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    });
    const out = [];
    roots.forEach((r) => {
      out.push({ ...r, depth: 0 });
      (byParent[r.id] || []).forEach((s) => out.push({ ...s, depth: 1 }));
    });
    const seen = new Set(out.map((x) => x.id));
    flat.forEach((c) => {
      if (!seen.has(c.id)) out.push({ ...c, depth: 1 });
    });
    return out;
  }, [categories]);

  const childCountByRootId = useMemo(() => {
    const m = {};
    categories.forEach((c) => {
      if (c.id != null && c.parent_id != null && c.parent_id !== '') {
        const k = Number(c.parent_id);
        if (!Number.isNaN(k)) m[k] = (m[k] || 0) + 1;
      }
    });
    return m;
  }, [categories]);

  const rootIdsWithChildren = useMemo(() => {
    const s = new Set();
    Object.keys(childCountByRootId).forEach((k) => s.add(Number(k)));
    return s;
  }, [childCountByRootId]);

  /** Filas visibles según raíces contraídas. */
  const categoryRowsOrdered = useMemo(() => {
    return categoryRowsFull.filter((c) => {
      if (!c.parent_id) return true;
      const pid = Number(c.parent_id);
      return !collapsedRootIds.has(pid);
    });
  }, [categoryRowsFull, collapsedRootIds]);

  const toggleRootCollapsed = (rootId) => {
    const id = Number(rootId);
    setCollapsedRootIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAllCategoryRoots = () => setCollapsedRootIds(new Set());

  const collapseAllCategoryRoots = () => {
    setCollapsedRootIds(new Set(rootIdsWithChildren));
  };

  const categoryOptions = useMemo(
    () => [
      { value: '', label: 'Todas las categorías' },
      ...categories
        .filter((c) => !c.parent_id)
        .map((c) => ({
          value: c.code,
          label: `${c.code} — ${c.name}${!isActiveFlag(c.is_active) ? ' (inactiva)' : ''}`,
        })),
    ],
    [categories],
  );

  const handleSortClick = (key) => {
    if (sortColumn === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDir('asc');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterCategory('');
  };

  const openProductModal = () => {
    setProductFormError('');
    setEditingProductId(null);
    setProductName('');
    setProductPrice('');
    setProductSpecLabel('');
    setCategoryMode('auto');
    setManualCategoryCode(activeCategoriesForSelect[0]?.code || 'UP04');
    setManualProductTypeCode('');
    setManualFormatCode(activeFormatsForSelect[0]?.format_code || 'CA');
    setManualBrandCode(activeBrandsForSelect[0]?.brand_code || 'MXX');
    setShowProductModal(true);
  };

  const openEditProduct = (row) => {
    if (!row?.id) return;
    setProductFormError('');
    setEditingProductId(row.id);
    setProductName(String(row.product_name || ''));
    setProductPrice(row.base_price != null ? String(row.base_price) : '');
    setProductSpecLabel(String(row.spec_label || ''));
    setCategoryMode('manual');
    setManualCategoryCode(String(row.category_code || activeCategoriesForSelect[0]?.code || 'UP04'));
    setManualProductTypeCode(String(row.product_type_code || '').padStart(2, '0'));
    setManualFormatCode(String(row.format_code || 'CA'));
    setManualBrandCode(String(row.brand_code || 'MXX'));
    setShowProductModal(true);
  };

  const handleDuplicateProduct = async (row) => {
    if (!row?.id) return;
    const baseName = String(row.product_name || '').trim() || 'Producto';
    const copyName = / \(copia\)$/i.test(baseName) ? baseName : `${baseName} (copia)`;
    try {
      const body = {
        product_name: copyName,
        base_price: Number(row.base_price) || 0,
        category_code: String(row.category_code || '').trim(),
        product_type_code: String(row.product_type_code || '').trim(),
        format_code: String(row.format_code || 'CA').trim() || 'CA',
        brand_code: String(row.brand_code || 'MXX').trim() || 'MXX',
        spec_label: String(row.spec_label || '').trim(),
      };
      await apiClient(`${API_BASE_URL}/api/quotations/upselling-products`, {
method: 'POST',
body: JSON.stringify(body)
      });
      await loadProducts();
    } catch (e) {
      window.alert(e?.message || 'No se pudo duplicar el producto.');
    }
  };

  const handleDeleteProduct = async (row) => {
    if (!row?.id) return;
    const ok = window.confirm(
      `¿Eliminar el producto «${row.product_name || row.upselling_code}»?\n\nSe dará de baja del catálogo (soft delete).`
    );
    if (!ok) return;
    try {
      await apiClient(`${API_BASE_URL}/api/quotations/upselling-products/${row.id}`, {
method: 'DELETE'
      });
      await loadProducts();
    } catch (e) {
      window.alert(e?.message || 'No se pudo eliminar el producto.');
    }
  };

  const openCategoryModal = (parentId = '') => {
    setCategoryFormError('');
    setNewCatCode('');
    setNewCatName('');
    setNewCategoryParentId(parentId === '' || parentId == null ? '' : String(parentId));
    setShowCategoryModal(true);
  };

  const handleSaveProduct = async () => {
    setProductFormError('');
    const name = String(productName || '').trim();
    if (!name) {
      setProductFormError('Indica el nombre del producto.');
      return;
    }
    const price = parseFloat(String(productPrice || '').replace(',', '.')) || 0;
    if (categoryMode === 'manual' && !String(manualCategoryCode || '').trim()) {
      setProductFormError('Selecciona una categoría.');
      return;
    }
    setSaveProductLoading(true);
    try {
      const body = {
        product_name: name,
        base_price: price,
        category_code: categoryMode === 'manual' ? String(manualCategoryCode).trim() : '',
        product_type_code:
          categoryMode === 'manual' ? String(manualProductTypeCode || '').trim() : '',
        format_code: String(manualFormatCode || 'CA').trim() || 'CA',
        brand_code: String(manualBrandCode || 'MXX').trim() || 'MXX',
        spec_label: String(productSpecLabel || '').trim(),
      };
      const isEdit = editingProductId != null;
      await apiClient(isEdit
          ? `${API_BASE_URL}/api/quotations/upselling-products/${editingProductId}`
          : `${API_BASE_URL}/api/quotations/upselling-products`, {
method: isEdit ? 'PUT' : 'POST',
body: JSON.stringify(body)
      });
      setShowProductModal(false);
      setEditingProductId(null);
      await loadProducts();
    } catch (e) {
      setProductFormError(e?.message || 'No se pudo guardar el producto.');
    } finally {
      setSaveProductLoading(false);
    }
  };

  const handleSaveCategory = async () => {
    setCategoryFormError('');
    const code = String(newCatCode || '').trim().toUpperCase();
    const displayName = String(newCatName || '').trim();
    if (code.length < 2) {
      setCategoryFormError('El código debe tener al menos 2 caracteres.');
      return;
    }
    if (!displayName) {
      setCategoryFormError('Indica el nombre de la categoría.');
      return;
    }
    setSaveCategoryLoading(true);
    try {
      const body = { category_code: code, display_name: displayName };
      if (newCategoryParentId) {
        const pid = Number(newCategoryParentId);
        if (Number.isFinite(pid) && pid > 0) body.parent_id = pid;
      }
      await apiClient(`${API_BASE_URL}/api/quotations/upselling-products/categories`, {
method: 'POST',
body: JSON.stringify(body)
      });
      setShowCategoryModal(false);
      await loadCategories();
      setManualCategoryCode(code);
    } catch (e) {
      setCategoryFormError(e?.message || 'No se pudo crear la categoría.');
    } finally {
      setSaveCategoryLoading(false);
    }
  };

  const openEditCategory = (c) => {
    if (c.id == null) return;
    setEditCategoryFormError('');
    setEditCategoryId(c.id);
    setEditCategoryCode(String(c.code || ''));
    setEditDisplayName(String(c.name || ''));
    setEditSortOrder(String(c.sort_order ?? 0));
    setEditIsActive(isActiveFlag(c.is_active));
    const hasKids = categories.some((x) => x.parent_id === c.id);
    setEditParentId(hasKids ? '' : c.parent_id != null ? String(c.parent_id) : '');
    setShowEditCategoryModal(true);
  };

  const handleSaveEditCategory = async () => {
    if (editCategoryId == null) return;
    setEditCategoryFormError('');
    const name = String(editDisplayName || '').trim();
    if (!name) {
      setEditCategoryFormError('Indica el nombre.');
      return;
    }
    const sortNum = parseInt(String(editSortOrder || '0'), 10);
    if (Number.isNaN(sortNum)) {
      setEditCategoryFormError('Orden inválido.');
      return;
    }
    const codeUp = String(editCategoryCode || '').trim().toUpperCase();
    if (codeUp.length < 2 || codeUp.length > 10) {
      setEditCategoryFormError('El código debe tener entre 2 y 10 caracteres.');
      return;
    }
    setSaveEditCategoryLoading(true);
    try {
      const payload = {
        display_name: name,
        sort_order: sortNum,
        is_active: editIsActive ? 1 : 0,
        category_code: codeUp,
      };
      if (editCategoryHasChildren) {
        payload.parent_id = null;
      } else if (editParentId === '') {
        payload.parent_id = null;
      } else {
        const pid = Number(editParentId);
        if (!Number.isFinite(pid) || pid <= 0) {
          setEditCategoryFormError('Selecciona un padre válido o raíz.');
          setSaveEditCategoryLoading(false);
          return;
        }
        payload.parent_id = pid;
      }
      await apiClient(`${API_BASE_URL}/api/quotations/upselling-products/categories/${editCategoryId}`, {
method: 'PUT',
body: JSON.stringify(payload)
      });
      setShowEditCategoryModal(false);
      await loadCategories();
    } catch (e) {
      setEditCategoryFormError(e?.message || 'No se pudo guardar.');
    } finally {
      setSaveEditCategoryLoading(false);
    }
  };

  const handleDeleteCategory = async (c) => {
    if (c.id == null) return;
    const ok = window.confirm(
      `¿Eliminar la categoría «${c.code} — ${c.name}»?\n\nSi tiene productos asociados, solo se dará de baja (inactiva). Si no tiene productos, se borrará del sistema.`
    );
    if (!ok) return;
    try {
      const data = await apiClient(
        `${API_BASE_URL}/api/quotations/upselling-products/categories/${c.id}`,
        { method: 'DELETE' }
      );
      if (data?.message) {
        window.alert(data.message);
      }
      await loadCategories();
      await loadProducts();
    } catch (e) {
      window.alert(e?.message || 'No se pudo eliminar.');
    }
  };

  const openFormatModal = () => {
    setFormatFormError('');
    setNewFormatCode('');
    setNewFormatName('');
    setShowFormatModal(true);
  };

  const handleSaveFormat = async () => {
    setFormatFormError('');
    const code = String(newFormatCode || '').trim().toUpperCase();
    const displayName = String(newFormatName || '').trim();
    if (code.length < 2) {
      setFormatFormError('El código debe tener al menos 2 caracteres.');
      return;
    }
    if (!displayName) {
      setFormatFormError('Indica el nombre del formato.');
      return;
    }
    setSaveFormatLoading(true);
    try {
      const data = await apiClient(`${API_BASE_URL}/api/quotations/upselling-products/formats`, {
method: 'POST',
body: JSON.stringify({ format_code: code, display_name: displayName })
      });
      setShowFormatModal(false);
      await loadFormats();
    } catch (e) {
      setFormatFormError(e?.message || 'No se pudo crear el formato.');
    } finally {
      setSaveFormatLoading(false);
    }
  };

  const openEditFormat = (f) => {
    if (f.id == null) return;
    setEditFormatFormError('');
    setEditFormatId(f.id);
    setEditFormatCode(String(f.format_code || ''));
    setEditFormatName(String(f.display_name || ''));
    setEditFormatSortOrder(String(f.sort_order ?? 0));
    setEditFormatIsActive(isActiveFlag(f.is_active));
    setShowEditFormatModal(true);
  };

  const handleSaveEditFormat = async () => {
    if (editFormatId == null) return;
    setEditFormatFormError('');
    const name = String(editFormatName || '').trim();
    if (!name) {
      setEditFormatFormError('Indica el nombre.');
      return;
    }
    const sortNum = parseInt(String(editFormatSortOrder || '0'), 10);
    if (Number.isNaN(sortNum)) {
      setEditFormatFormError('Orden inválido.');
      return;
    }
    const codeUp = String(editFormatCode || '').trim().toUpperCase();
    if (codeUp.length < 2 || codeUp.length > 12) {
      setEditFormatFormError('El código debe tener entre 2 y 12 caracteres.');
      return;
    }
    setSaveEditFormatLoading(true);
    try {
      await apiClient(`${API_BASE_URL}/api/quotations/upselling-products/formats/${editFormatId}`, {
method: 'PUT',
body: JSON.stringify({
            display_name: name,
            sort_order: sortNum,
            is_active: editFormatIsActive ? 1 : 0,
            format_code: codeUp,
          })
      });
      setShowEditFormatModal(false);
      await loadFormats();
    } catch (e) {
      setEditFormatFormError(e?.message || 'No se pudo guardar.');
    } finally {
      setSaveEditFormatLoading(false);
    }
  };

  const handleDeleteFormat = async (f) => {
    if (f.id == null) return;
    const ok = window.confirm(
      `¿Eliminar el formato «${f.format_code} — ${f.display_name}»?`
    );
    if (!ok) return;
    try {
      await apiClient(`${API_BASE_URL}/api/quotations/upselling-products/formats/${f.id}`, {
        method: 'DELETE',
      });
      await loadFormats();
    } catch (e) {
      window.alert(e?.message || 'No se pudo eliminar.');
    }
  };

  const openBrandModal = () => {
    setBrandFormError('');
    setNewBrandCode('M');
    setNewBrandName('');
    setShowBrandModal(true);
  };

  const handleSaveBrand = async () => {
    setBrandFormError('');
    const code = String(newBrandCode || '').trim().toUpperCase();
    const displayName = String(newBrandName || '').trim();
    if (!/^M[A-Z0-9]{2}$/.test(code)) {
      setBrandFormError('El código debe ser M + 2 caracteres (ej. M26). Sin marca: MXX.');
      return;
    }
    if (!displayName) {
      setBrandFormError('Indica el nombre de la marca.');
      return;
    }
    setSaveBrandLoading(true);
    try {
      await apiClient(`${API_BASE_URL}/api/quotations/upselling-products/brands`, {
        method: 'POST',
        body: JSON.stringify({ brand_code: code, display_name: displayName }),
      });
      setShowBrandModal(false);
      await loadBrands();
    } catch (e) {
      setBrandFormError(e?.message || 'No se pudo crear la marca.');
    } finally {
      setSaveBrandLoading(false);
    }
  };

  const openEditBrand = (b) => {
    if (b.id == null) return;
    setEditBrandFormError('');
    setEditBrandId(b.id);
    setEditBrandCode(String(b.brand_code || ''));
    setEditBrandName(String(b.display_name || ''));
    setEditBrandSortOrder(String(b.sort_order ?? 0));
    setEditBrandIsActive(isActiveFlag(b.is_active));
    setShowEditBrandModal(true);
  };

  const handleSaveEditBrand = async () => {
    if (editBrandId == null) return;
    setEditBrandFormError('');
    const name = String(editBrandName || '').trim();
    if (!name) {
      setEditBrandFormError('Indica el nombre.');
      return;
    }
    const sortNum = parseInt(String(editBrandSortOrder || '0'), 10);
    if (Number.isNaN(sortNum)) {
      setEditBrandFormError('Orden inválido.');
      return;
    }
    const codeUp = String(editBrandCode || '').trim().toUpperCase();
    if (!/^M[A-Z0-9]{2}$/.test(codeUp)) {
      setEditBrandFormError('El código debe ser M + 2 caracteres (ej. M26).');
      return;
    }
    setSaveEditBrandLoading(true);
    try {
      await apiClient(`${API_BASE_URL}/api/quotations/upselling-products/brands/${editBrandId}`, {
        method: 'PUT',
        body: JSON.stringify({
          display_name: name,
          sort_order: sortNum,
          is_active: editBrandIsActive ? 1 : 0,
          brand_code: codeUp,
        }),
      });
      setShowEditBrandModal(false);
      await loadBrands();
    } catch (e) {
      setEditBrandFormError(e?.message || 'No se pudo guardar.');
    } finally {
      setSaveEditBrandLoading(false);
    }
  };

  const handleDeleteBrand = async (b) => {
    if (b.id == null) return;
    const ok = window.confirm(
      `¿Eliminar o dar de baja la marca «${b.brand_code} — ${b.display_name}»?`
    );
    if (!ok) return;
    try {
      await apiClient(`${API_BASE_URL}/api/quotations/upselling-products/brands/${b.id}`, {
        method: 'DELETE',
      });
      await loadBrands();
    } catch (e) {
      window.alert(e?.message || 'No se pudo eliminar.');
    }
  };

  const formatsRowsOrdered = useMemo(() => {
    const arr = [...formats];
    arr.sort((a, b) => {
      const o = (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
      if (o !== 0) return o;
      return String(a.display_name || '').localeCompare(String(b.display_name || ''), 'es');
    });
    return arr;
  }, [formats]);

  const brandsRowsOrdered = useMemo(() => {
    const arr = [...brands];
    arr.sort((a, b) => {
      const o = (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
      if (o !== 0) return o;
      return String(a.display_name || '').localeCompare(String(b.display_name || ''), 'es');
    });
    return arr;
  }, [brands]);

  const hasActiveFilters = Boolean(searchQuery.trim()) || Boolean(filterCategory);
  const productsSubtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${filteredRows.length} de ${rows.length} productos · A012_upselling_products`
        : `${rows.length} productos · A012_upselling_products`);

  const categoriesSubtitle = `${categories.filter((x) => x.id != null).length} categorías · A015 (subcategorías bajo la principal)`;

  const formatsSubtitle = formatsLoading || formatsSyncing
    ? formatsSyncing
      ? 'Sincronizando formatos desde Pinturas…'
      : 'Cargando formatos…'
    : `${formats.length} formatos · A016_upselling_formats`;

  const brandsSubtitle = brandsLoading
    ? 'Cargando marcas…'
    : `${brands.length} marcas · A016_upselling_brands`;

  const colCount = TABLE_COLUMNS.length;

  const hierarchyToggleBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '22px',
    padding: 0,
    border: 'none',
    borderRadius: '6px',
    background: 'transparent',
    color: muted,
    cursor: 'pointer',
    flexShrink: 0,
  };

  const renderProductItemRow = (row) => (
    <tr
      key={row.id}
      style={{
        borderBottom: `1px solid ${border}`,
        transition: 'background 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = rowHover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <td
        style={{
          padding: `${padV}px ${padH}px`,
          paddingLeft: `${padH + HIERARCHY_ITEM_SKU_INDENT}px`,
          verticalAlign: 'middle',
        }}
      >
        <div style={{ fontWeight: 700, fontSize: '12px', color: '#2563eb', lineHeight: 1.2 }}>
          {row.upselling_code || '—'}
        </div>
      </td>
      <td
        style={{
          padding: `${padV}px ${padH}px`,
          paddingLeft: `${padH + HIERARCHY_ITEM_PRODUCT_INDENT}px`,
          verticalAlign: 'middle',
          maxWidth: '360px',
        }}
      >
        <div style={{ fontSize: '12px', fontWeight: 500, color: text, lineHeight: 1.25 }}>
          {row.product_name || '—'}
        </div>
      </td>
      <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
        <div
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: text,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            lineHeight: 1.2,
          }}
        >
          {row.format_code || '—'}
        </div>
        <div style={{ fontSize: '10px', color: muted, lineHeight: 1.2 }}>
          {formats.find((f) => String(f.format_code) === String(row.format_code))?.display_name || '—'}
        </div>
      </td>
      <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
        <div
          style={{
            fontSize: '12px',
            fontWeight: 600,
            color: text,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            lineHeight: 1.2,
          }}
        >
          {row.brand_code || 'MXX'}
        </div>
        <div style={{ fontSize: '10px', color: muted, lineHeight: 1.2 }}>
          {brands.find((b) => String(b.brand_code) === String(row.brand_code || 'MXX'))?.display_name ||
            (String(row.brand_code || 'MXX') === 'MXX' ? 'Sin marca' : '—')}
        </div>
      </td>
      <td
        style={{
          padding: `${padV}px ${padH}px`,
          verticalAlign: 'middle',
          textAlign: 'right',
          whiteSpace: 'nowrap',
        }}
      >
        {(() => {
          const minP = Number(row.min_quoted_price);
          const maxP = Number(row.max_quoted_price);
          const hasRange = Number.isFinite(minP) && Number.isFinite(maxP) && (row.quoted_count || 0) > 0;
          if (!hasRange) {
            return (
              <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.2 }}>
                {formatMoney(row.base_price)}
              </div>
            );
          }
          const same = Math.abs(minP - maxP) < 0.005;
          return (
            <>
              <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.2 }}>
                {same ? formatMoney(minP) : `${formatMoney(minP)} – ${formatMoney(maxP)}`}
              </div>
              <div style={{ fontSize: '10px', color: muted, lineHeight: 1.2 }}>
                {row.base_price != null && Number(row.base_price) > 0
                  ? `cat. ${formatMoney(row.base_price)}`
                  : ''}
              </div>
            </>
          );
        })()}
      </td>
      <td
        style={{
          padding: `${padV}px ${padH}px`,
          verticalAlign: 'middle',
          textAlign: 'right',
          whiteSpace: 'nowrap',
        }}
      >
        <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.2 }}>
          {Number(row.quoted_count) || 0}
        </div>
        <div style={{ fontSize: '10px', color: muted, lineHeight: 1.2 }}>veces</div>
      </td>
      <td
        style={{
          padding: `${padV}px ${padH}px`,
          verticalAlign: 'middle',
          textAlign: 'right',
          whiteSpace: 'nowrap',
        }}
      >
        {(() => {
          const formatQty = (value) => {
            const qty = Number(value) || 0;
            return Math.abs(qty - Math.round(qty)) < 0.001
              ? Math.round(qty).toLocaleString('es-CL')
              : qty.toLocaleString('es-CL', { maximumFractionDigits: 2 });
          };
          return (
            <>
              <div style={{ fontSize: '12px', fontWeight: 600, color: text, lineHeight: 1.2 }}>
                {formatQty(row.quoted_quantity)} uds
              </div>
              <div style={{ fontSize: '10px', color: muted, lineHeight: 1.2 }}>
                ({formatQty(row.sold_quantity)} vend.)
              </div>
            </>
          );
        })()}
      </td>
      <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'middle', textAlign: 'center' }}>
        {renderStatusBadge(row.status)}
      </td>
      <td
        style={{
          padding: `${padV}px ${padH}px`,
          verticalAlign: 'middle',
          textAlign: 'right',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ display: 'inline-flex', flexWrap: 'nowrap', gap: '4px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => openEditProduct(row)}
            title="Editar"
            aria-label="Editar"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              padding: 0,
              borderRadius: '6px',
              border: `1px solid ${border}`,
              background: inputBg,
              color: text,
              cursor: 'pointer',
            }}
          >
            <Pencil size={14} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => handleDuplicateProduct(row)}
            title="Duplicar"
            aria-label="Duplicar"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              padding: 0,
              borderRadius: '6px',
              border: `1px solid ${border}`,
              background: inputBg,
              color: text,
              cursor: 'pointer',
            }}
          >
            <Copy size={14} aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => handleDeleteProduct(row)}
            title="Eliminar"
            aria-label="Eliminar"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              padding: 0,
              borderRadius: '6px',
              border: '1px solid #fecaca',
              background: isDarkMode ? '#450a0a' : '#fef2f2',
              color: '#b91c1c',
              cursor: 'pointer',
            }}
          >
            <Trash2 size={14} aria-hidden />
          </button>
        </span>
      </td>
    </tr>
  );

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: '12px',
        overflow: 'hidden',
        background: bg,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ flex: '1 1 200px', display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>Upselling</h3>
          <p style={{ margin: 0, fontSize: '12px', color: muted }}>
            {workspaceTab === 'products'
              ? productsSubtitle
              : workspaceTab === 'categories'
                ? categoriesSubtitle
                : workspaceTab === 'formats'
                  ? formatsSubtitle
                  : brandsSubtitle}
          </p>
        </div>
        <div
          style={{
            display: 'inline-flex',
            borderRadius: '8px',
            border: `1px solid ${border}`,
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={() => setWorkspaceTab('products')}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: workspaceTab === 'products' ? (isDarkMode ? '#27272a' : '#e8e0d4') : inputBg,
              color: text,
              fontSize: '12px',
              fontWeight: workspaceTab === 'products' ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            Productos
          </button>
          <button
            type="button"
            onClick={() => setWorkspaceTab('categories')}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderLeft: `1px solid ${border}`,
              background: workspaceTab === 'categories' ? (isDarkMode ? '#27272a' : '#e8e0d4') : inputBg,
              color: text,
              fontSize: '12px',
              fontWeight: workspaceTab === 'categories' ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            Categorías
          </button>
          <button
            type="button"
            onClick={() => setWorkspaceTab('formats')}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderLeft: `1px solid ${border}`,
              background: workspaceTab === 'formats' ? (isDarkMode ? '#27272a' : '#e8e0d4') : inputBg,
              color: text,
              fontSize: '12px',
              fontWeight: workspaceTab === 'formats' ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            Formatos
          </button>
          <button
            type="button"
            onClick={() => setWorkspaceTab('brands')}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderLeft: `1px solid ${border}`,
              background: workspaceTab === 'brands' ? (isDarkMode ? '#27272a' : '#e8e0d4') : inputBg,
              color: text,
              fontSize: '12px',
              fontWeight: workspaceTab === 'brands' ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            Marcas
          </button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {workspaceTab === 'categories' ? (
            <button
              type="button"
              onClick={() => openCategoryModal('')}
              style={{
                height: '34px',
                padding: '0 14px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Plus size={15} aria-hidden />
              Nueva categoría
            </button>
          ) : workspaceTab === 'formats' ? (
            <>
              <button
                type="button"
                onClick={() => syncFormatsFromPaint({ silent: false })}
                disabled={formatsSyncing || formatsLoading}
                style={{
                  height: '34px',
                  padding: '0 14px',
                  borderRadius: '8px',
                  border: `1px solid ${border}`,
                  background: inputBg,
                  color: text,
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: formatsSyncing || formatsLoading ? 'wait' : 'pointer',
                }}
              >
                {formatsSyncing ? 'Sincronizando…' : 'Sincronizar desde Pinturas'}
              </button>
              <button
                type="button"
                onClick={openFormatModal}
                style={{
                  height: '34px',
                  padding: '0 14px',
                  borderRadius: '8px',
                  border: `1px solid ${border}`,
                  background: inputBg,
                  color: text,
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Plus size={15} aria-hidden />
                Nuevo formato
              </button>
            </>
          ) : workspaceTab === 'brands' ? (
            <button
              type="button"
              onClick={openBrandModal}
              style={{
                height: '34px',
                padding: '0 14px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Plus size={15} aria-hidden />
              Nueva marca
            </button>
          ) : (
            <button
              type="button"
              onClick={openProductModal}
              style={{
                height: '34px',
                padding: '0 14px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Plus size={15} aria-hidden />
              Nuevo producto
            </button>
          )}
        </div>
      </div>

      {workspaceTab === 'products' ? (
      <>
      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Buscar</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="SKU, producto, categoría…"
              style={{ ...selectStyle, flex: 1, minWidth: 0, height: '36px' }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '200px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>Categoría</span>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} style={selectStyle}>
            {categoryOptions.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={expandAllProductCategories}
          disabled={productHierarchy.length === 0}
          style={{
            height: '36px',
            padding: '0 12px',
            borderRadius: '8px',
            border: `1px solid ${inputBorder}`,
            background: inputBg,
            color: text,
            fontSize: '12px',
            fontWeight: 600,
            cursor: productHierarchy.length === 0 ? 'not-allowed' : 'pointer',
            opacity: productHierarchy.length === 0 ? 0.5 : 1,
            marginBottom: '2px',
          }}
        >
          Expandir categorías
        </button>
        <button
          type="button"
          onClick={collapseAllProductCategories}
          disabled={productHierarchy.length === 0}
          style={{
            height: '36px',
            padding: '0 12px',
            borderRadius: '8px',
            border: `1px solid ${inputBorder}`,
            background: inputBg,
            color: text,
            fontSize: '12px',
            fontWeight: 600,
            cursor: productHierarchy.length === 0 ? 'not-allowed' : 'pointer',
            opacity: productHierarchy.length === 0 ? 0.5 : 1,
            marginBottom: '2px',
          }}
        >
          Contraer categorías
        </button>
        <button
          type="button"
          onClick={expandAllProductTypes}
          disabled={allProductTypeKeys.size === 0}
          style={{
            height: '36px',
            padding: '0 12px',
            borderRadius: '8px',
            border: `1px solid ${inputBorder}`,
            background: inputBg,
            color: text,
            fontSize: '12px',
            fontWeight: 600,
            cursor: allProductTypeKeys.size === 0 ? 'not-allowed' : 'pointer',
            opacity: allProductTypeKeys.size === 0 ? 0.5 : 1,
            marginBottom: '2px',
          }}
        >
          Expandir productos
        </button>
        <button
          type="button"
          onClick={collapseAllProductTypes}
          disabled={allProductTypeKeys.size === 0}
          style={{
            height: '36px',
            padding: '0 12px',
            borderRadius: '8px',
            border: `1px solid ${inputBorder}`,
            background: inputBg,
            color: text,
            fontSize: '12px',
            fontWeight: 600,
            cursor: allProductTypeKeys.size === 0 ? 'not-allowed' : 'pointer',
            opacity: allProductTypeKeys.size === 0 ? 0.5 : 1,
            marginBottom: '2px',
          }}
        >
          Contraer productos
        </button>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: muted,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: '2px',
            }}
          >
            Limpiar filtros
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => {
            loadProducts();
            loadCategories();
            loadFormats();
          }}
          disabled={loading}
          style={{
            height: '36px',
            padding: '0 12px',
            borderRadius: '8px',
            border: `1px solid ${inputBorder}`,
            background: inputBg,
            color: text,
            fontSize: '12px',
            fontWeight: 600,
            cursor: loading ? 'wait' : 'pointer',
            marginBottom: '2px',
          }}
        >
          Actualizar
        </button>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
          <thead>
            <tr style={{ background: headerBg }}>
              {TABLE_COLUMNS.map(({ key: h, align, sortKey: sk }) => (
                <th
                  key={h}
                  scope="col"
                  onClick={() => sk && handleSortClick(sk)}
                  onKeyDown={(e) => {
                    if (!sk) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSortClick(sk);
                    }
                  }}
                  tabIndex={sk ? 0 : undefined}
                  style={{
                    textAlign: align,
                    padding: `${thPadV}px ${padH + 4}px`,
                    fontSize: '11px',
                    fontWeight: 700,
                    color: muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    borderBottom: `1px solid ${border}`,
                    cursor: sk ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
                      gap: '6px',
                      width: align === 'center' || align === 'right' ? '100%' : 'auto',
                    }}
                  >
                    {h}
                    {sk ? (
                      sortColumn === sk ? (
                        sortDir === 'asc' ? (
                          <ArrowUp size={13} strokeWidth={2.5} aria-hidden />
                        ) : (
                          <ArrowDown size={13} strokeWidth={2.5} aria-hidden />
                        )
                      ) : (
                        <ArrowUpDown size={13} strokeWidth={2} style={{ opacity: 0.4 }} aria-hidden />
                      )
                    ) : null}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  Cargando productos…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  {error || 'No hay productos. Usa «Nuevo producto» para agregar al catálogo (A012).'}
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  Ningún resultado con los filtros actuales.
                </td>
              </tr>
            ) : (
              displayProductRows.map((entry) => {
                if (entry.kind === 'category') {
                  const isCollapsed = entry.collapsed;
                  return (
                    <tr
                      key={entry.key}
                      style={{
                        borderBottom: `1px solid ${border}`,
                        background: headerBg,
                      }}
                    >
                      <td colSpan={colCount} style={{ padding: `${padV + 2}px ${padH}px` }}>
                        <button
                          type="button"
                          aria-expanded={!isCollapsed}
                          aria-label={isCollapsed ? 'Expandir categoría' : 'Contraer categoría'}
                          onClick={() => toggleProductCategoryCollapsed(entry.code)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            width: '100%',
                            padding: 0,
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            textAlign: 'left',
                            color: text,
                          }}
                        >
                          <span style={hierarchyToggleBtnStyle}>
                            {isCollapsed ? (
                              <ChevronRight size={16} aria-hidden />
                            ) : (
                              <ChevronDown size={16} aria-hidden />
                            )}
                          </span>
                          <span style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.02em' }}>
                            {entry.code} — {entry.name}
                          </span>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: muted }}>
                            {entry.typeCount} productos · {entry.itemCount} variantes
                          </span>
                        </button>
                      </td>
                    </tr>
                  );
                }

                if (entry.kind === 'productType') {
                  const isCollapsed = entry.collapsed;
                  return (
                    <tr
                      key={entry.key}
                      style={{
                        borderBottom: `1px solid ${border}`,
                        background: rowHover,
                      }}
                    >
                      <td colSpan={colCount} style={{ padding: `${padV}px ${padH}px`, paddingLeft: `${padH + HIERARCHY_INDENT_PRODUCT}px` }}>
                        <button
                          type="button"
                          aria-expanded={!isCollapsed}
                          aria-label={isCollapsed ? 'Expandir producto' : 'Contraer producto'}
                          onClick={() => toggleProductTypeCollapsed(entry.key)}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            width: '100%',
                            padding: 0,
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            textAlign: 'left',
                            color: text,
                          }}
                        >
                          <span style={hierarchyToggleBtnStyle}>
                            {isCollapsed ? (
                              <ChevronRight size={15} aria-hidden />
                            ) : (
                              <ChevronDown size={15} aria-hidden />
                            )}
                          </span>
                          <span style={{ fontSize: '12px', fontWeight: 600 }}>
                            {entry.name} ({entry.code})
                          </span>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: muted }}>
                            {entry.items.length} variantes
                          </span>
                        </button>
                      </td>
                    </tr>
                  );
                }

                return renderProductItemRow(entry.row);
              })
            )}
          </tbody>
        </table>
      </div>
      </>
      ) : workspaceTab === 'categories' ? (
      <div style={{ padding: '0 0 12px', background: bg }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            alignItems: 'center',
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            gap: '8px',
          }}
        >
          <button
            type="button"
            onClick={expandAllCategoryRoots}
            disabled={rootIdsWithChildren.size === 0}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: '12px',
              fontWeight: 600,
              cursor: rootIdsWithChildren.size === 0 ? 'not-allowed' : 'pointer',
              opacity: rootIdsWithChildren.size === 0 ? 0.5 : 1,
            }}
          >
            Expandir todas
          </button>
          <button
            type="button"
            onClick={collapseAllCategoryRoots}
            disabled={rootIdsWithChildren.size === 0}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: '12px',
              fontWeight: 600,
              cursor: rootIdsWithChildren.size === 0 ? 'not-allowed' : 'pointer',
              opacity: rootIdsWithChildren.size === 0 ? 0.5 : 1,
            }}
          >
            Contraer todas
          </button>
          <button
            type="button"
            onClick={() => loadCategories()}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Actualizar lista
          </button>
        </div>
        <div style={{ overflowX: 'auto', padding: '0 16px 12px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '720px' }}>
            <thead>
              <tr style={{ background: headerBg }}>
                {['Código', 'Nombre', 'Padre', 'Orden', 'Estado', 'Acciones'].map((label) => (
                  <th
                    key={label}
                    style={{
                      textAlign: label === 'Orden' ? 'right' : label === 'Estado' ? 'center' : label === 'Acciones' ? 'right' : 'left',
                      padding: `${thPadV}px ${padH + 4}px`,
                      fontSize: '11px',
                      fontWeight: 700,
                      color: muted,
                      textTransform: 'uppercase',
                      borderBottom: `1px solid ${border}`,
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categoryRowsOrdered.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '20px', textAlign: 'center', color: muted }}>
                    No hay categorías en base de datos.
                  </td>
                </tr>
              ) : (
                categoryRowsOrdered.map((c) => {
                  const canManage = c.id != null;
                  const isActive = isActiveFlag(c.is_active);
                  const depth = c.depth || 0;
                  const isRoot = !c.parent_id;
                  const rid = c.id != null ? Number(c.id) : null;
                  const hasSubcategories =
                    isRoot && rid != null && (childCountByRootId[rid] ?? 0) > 0;
                  const isCollapsed = rid != null && collapsedRootIds.has(rid);
                  return (
                    <tr
                      key={c.id != null ? `id-${c.id}` : `code-${c.code}`}
                      style={{ borderBottom: `1px solid ${border}` }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = rowHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <td
                        style={{
                          padding: `${padV}px ${padH}px`,
                          fontWeight: 700,
                          fontSize: '12px',
                          color: text,
                          paddingLeft: `${padH + depth * 18}px`,
                        }}
                      >
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            verticalAlign: 'middle',
                          }}
                        >
                          {hasSubcategories ? (
                            <button
                              type="button"
                              aria-expanded={!isCollapsed}
                              aria-label={
                                isCollapsed ? 'Expandir subcategorías' : 'Contraer subcategorías'
                              }
                              title={
                                isCollapsed ? 'Mostrar subcategorías' : 'Ocultar subcategorías'
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleRootCollapsed(rid);
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 26,
                                height: 26,
                                padding: 0,
                                borderRadius: '6px',
                                border: `1px solid ${border}`,
                                background: inputBg,
                                color: text,
                                cursor: 'pointer',
                                flexShrink: 0,
                              }}
                            >
                              {isCollapsed ? (
                                <ChevronRight size={16} strokeWidth={2.25} />
                              ) : (
                                <ChevronDown size={16} strokeWidth={2.25} />
                              )}
                            </button>
                          ) : (
                            <span
                              style={{
                                display: 'inline-block',
                                width: 26,
                                height: 26,
                                flexShrink: 0,
                              }}
                              aria-hidden
                            />
                          )}
                          <span>
                            {depth > 0 ? '↳ ' : ''}
                            {c.code}
                          </span>
                        </span>
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: text }}>{c.name}</td>
                      <td style={{ padding: `${padV}px ${padH}px`, fontSize: '11px', color: muted }}>
                        {c.parent_code || '—'}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'right', fontSize: '12px', color: muted }}>
                        {c.sort_order ?? '—'}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                        {renderStatusBadge(isActive ? 'Activo' : 'Inactivo')}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {canManage ? (
                          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-end' }}>
                            {isRoot && isActive ? (
                              <button
                                type="button"
                                onClick={() => openCategoryModal(c.id)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '6px 10px',
                                  borderRadius: '6px',
                                  border: `1px solid ${border}`,
                                  background: inputBg,
                                  color: text,
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                }}
                              >
                                <Plus size={14} aria-hidden />
                                Subcategoría
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => openEditCategory(c)}
                              title="Editar"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '6px 10px',
                                borderRadius: '6px',
                                border: `1px solid ${border}`,
                                background: inputBg,
                                color: text,
                                fontSize: '11px',
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              <Pencil size={14} aria-hidden />
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteCategory(c)}
                              title="Eliminar o dar de baja"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '6px 10px',
                                borderRadius: '6px',
                                border: '1px solid #fecaca',
                                background: isDarkMode ? '#450a0a' : '#fef2f2',
                                color: '#b91c1c',
                                fontSize: '11px',
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                              <Trash2 size={14} aria-hidden />
                              Eliminar
                            </button>
                          </span>
                        ) : (
                          <span style={{ fontSize: '11px', color: muted }}>Solo lectura (catálogo por defecto)</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      ) : workspaceTab === 'formats' ? (
      <div style={{ padding: '0 0 12px', background: bg }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            alignItems: 'center',
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            gap: '8px',
          }}
        >
          <button
            type="button"
            onClick={() => loadFormats()}
            disabled={formatsLoading || formatsSyncing}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: '12px',
              fontWeight: 600,
              cursor: formatsLoading || formatsSyncing ? 'wait' : 'pointer',
            }}
          >
            Actualizar lista
          </button>
        </div>
        <div style={{ overflowX: 'auto', padding: '0 16px 12px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
            <thead>
              <tr style={{ background: headerBg }}>
                {['Código', 'Nombre', 'Orden', 'Estado', 'Acciones'].map((label) => (
                  <th
                    key={label}
                    style={{
                      textAlign:
                        label === 'Orden'
                          ? 'right'
                          : label === 'Estado'
                            ? 'center'
                            : label === 'Acciones'
                              ? 'right'
                              : 'left',
                      padding: `${thPadV}px ${padH + 4}px`,
                      fontSize: '11px',
                      fontWeight: 700,
                      color: muted,
                      textTransform: 'uppercase',
                      borderBottom: `1px solid ${border}`,
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {formatsLoading ? (
                <tr>
                  <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: muted }}>
                    Cargando formatos…
                  </td>
                </tr>
              ) : formatsRowsOrdered.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: muted }}>
                    No hay formatos. Usa «Nuevo formato» para agregarlos (A016).
                  </td>
                </tr>
              ) : (
                formatsRowsOrdered.map((f) => {
                  const isActive = isActiveFlag(f.is_active);
                  return (
                    <tr
                      key={f.id}
                      style={{ borderBottom: `1px solid ${border}` }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = rowHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <td
                        style={{
                          padding: `${padV}px ${padH}px`,
                          fontWeight: 700,
                          fontSize: '12px',
                          color: text,
                        }}
                      >
                        {f.format_code}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: text }}>
                        {f.display_name}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'right', fontSize: '12px', color: muted }}>
                        {f.sort_order ?? '—'}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                        {renderStatusBadge(isActive ? 'Activo' : 'Inactivo')}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            onClick={() => openEditFormat(f)}
                            title="Editar"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: `1px solid ${border}`,
                              background: inputBg,
                              color: text,
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            <Pencil size={14} aria-hidden />
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteFormat(f)}
                            title="Eliminar"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '1px solid #fecaca',
                              background: isDarkMode ? '#450a0a' : '#fef2f2',
                              color: '#b91c1c',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            <Trash2 size={14} aria-hidden />
                            Eliminar
                          </button>
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      ) : (
      <div style={{ padding: '0 0 12px', background: bg }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            alignItems: 'center',
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            gap: '8px',
          }}
        >
          <button
            type="button"
            onClick={() => loadBrands()}
            disabled={brandsLoading}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: text,
              fontSize: '12px',
              fontWeight: 600,
              cursor: brandsLoading ? 'wait' : 'pointer',
            }}
          >
            Actualizar lista
          </button>
        </div>
        <div style={{ overflowX: 'auto', padding: '0 16px 12px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
            <thead>
              <tr style={{ background: headerBg }}>
                {['Código', 'Nombre', 'Orden', 'Estado', 'Acciones'].map((label) => (
                  <th
                    key={label}
                    style={{
                      textAlign:
                        label === 'Orden'
                          ? 'right'
                          : label === 'Estado'
                            ? 'center'
                            : label === 'Acciones'
                              ? 'right'
                              : 'left',
                      padding: `${thPadV}px ${padH + 4}px`,
                      fontSize: '11px',
                      fontWeight: 700,
                      color: muted,
                      textTransform: 'uppercase',
                      borderBottom: `1px solid ${border}`,
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {brandsLoading ? (
                <tr>
                  <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: muted }}>
                    Cargando marcas…
                  </td>
                </tr>
              ) : brandsRowsOrdered.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: muted }}>
                    No hay marcas. Usa «Nueva marca» (código M + 2 caracteres, MXX = sin marca).
                  </td>
                </tr>
              ) : (
                brandsRowsOrdered.map((b) => {
                  const isActive = isActiveFlag(b.is_active);
                  return (
                    <tr
                      key={b.id}
                      style={{ borderBottom: `1px solid ${border}` }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = rowHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <td
                        style={{
                          padding: `${padV}px ${padH}px`,
                          fontWeight: 700,
                          fontSize: '12px',
                          color: text,
                        }}
                      >
                        {b.brand_code}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: text }}>
                        {b.display_name}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'right', fontSize: '12px', color: muted }}>
                        {b.sort_order ?? '—'}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                        {renderStatusBadge(isActive ? 'Activo' : 'Inactivo')}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            onClick={() => openEditBrand(b)}
                            title="Editar"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: `1px solid ${border}`,
                              background: inputBg,
                              color: text,
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            <Pencil size={14} aria-hidden />
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteBrand(b)}
                            title="Eliminar"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '1px solid #fecaca',
                              background: isDarkMode ? '#450a0a' : '#fef2f2',
                              color: '#b91c1c',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            <Trash2 size={14} aria-hidden />
                            Eliminar
                          </button>
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <Dialog
        isOpen={showProductModal}
        onClose={() => {
          if (saveProductLoading) return;
          setShowProductModal(false);
          setEditingProductId(null);
        }}
        title={editingProductId != null ? 'Editar producto de upselling' : 'Nuevo producto de upselling'}
        subtitle="SKU = Categoría(UP01–UP13) + Código producto(2) + Formato + Marca(MXX si no hay) + -correlativo especificación."
        size="md"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveProductLoading}
        closeOnEscape={!saveProductLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre del producto</span>
            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              disabled={saveProductLoading}
              placeholder='Ej. Espátula 4" acero'
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Especificación (opcional)</span>
            <input
              type="text"
              value={productSpecLabel}
              onChange={(e) => setProductSpecLabel(e.target.value)}
              disabled={saveProductLoading}
              placeholder='Ej. 4" / Thinner No. 2'
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Precio base (CLP)</span>
            <input
              type="number"
              min="0"
              step="1"
              value={productPrice}
              onChange={(e) => setProductPrice(e.target.value)}
              disabled={saveProductLoading}
              placeholder="0"
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <div style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Categoría (UP01–UP13)</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: text }}>
            <input
              type="radio"
              name="upCatMode"
              checked={categoryMode === 'auto'}
              onChange={() => setCategoryMode('auto')}
              disabled={saveProductLoading}
            />
            Automática (según palabras del nombre)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: text }}>
            <input
              type="radio"
              name="upCatMode"
              checked={categoryMode === 'manual'}
              onChange={() => setCategoryMode('manual')}
              disabled={saveProductLoading}
            />
            Manual
          </label>
          {categoryMode === 'manual' ? (
            <>
              <select
                value={manualCategoryCode}
                onChange={(e) => {
                  setManualCategoryCode(e.target.value);
                  setManualProductTypeCode('');
                }}
                disabled={saveProductLoading || activeCategoriesForSelect.length === 0}
                style={{ ...selectStyle, width: '100%' }}
              >
                {activeCategoriesForSelect.length === 0 ? (
                  <option value="">Crea una categoría activa primero</option>
                ) : (
                  activeCategoriesForSelect.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} — {c.name}
                    </option>
                  ))
                )}
              </select>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>
                  Código de producto (2 dígitos)
                </span>
                <select
                  value={manualProductTypeCode}
                  onChange={(e) => setManualProductTypeCode(e.target.value)}
                  disabled={saveProductLoading}
                  style={{ ...selectStyle, width: '100%' }}
                >
                  <option value="">Automático según nombre</option>
                  {productTypesForManualCategory.map((c) => (
                    <option key={c.id || c.code} value={c.code}>
                      {c.code} — {c.name}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Formato</span>
              <select
                value={manualFormatCode}
                onChange={(e) => setManualFormatCode(e.target.value)}
                disabled={saveProductLoading}
                style={{ ...selectStyle, width: '100%', minWidth: 0 }}
              >
                {(activeFormatsForSelect.length ? activeFormatsForSelect : [{ format_code: 'CA', display_name: 'Unidad' }]).map(
                  (f) => (
                    <option key={f.format_code} value={f.format_code}>
                      {f.format_code} — {f.display_name}
                    </option>
                  )
                )}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Marca</span>
              <select
                value={manualBrandCode}
                onChange={(e) => setManualBrandCode(e.target.value)}
                disabled={saveProductLoading}
                style={{ ...selectStyle, width: '100%', minWidth: 0 }}
              >
                {(activeBrandsForSelect.length
                  ? activeBrandsForSelect
                  : [{ brand_code: 'MXX', display_name: 'Sin marca' }]
                ).map((b) => (
                  <option key={b.brand_code} value={b.brand_code}>
                    {b.brand_code} — {b.display_name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {productFormError ? <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{productFormError}</p> : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveProductLoading}
              onClick={() => {
                setShowProductModal(false);
                setEditingProductId(null);
              }}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveProductLoading || (categoryMode === 'manual' && activeCategoriesForSelect.length === 0)}
              onClick={handleSaveProduct}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                opacity: saveProductLoading ? 0.75 : 1,
              }}
            >
              {saveProductLoading
                ? 'Guardando…'
                : editingProductId != null
                  ? 'Guardar cambios'
                  : 'Guardar producto'}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showCategoryModal}
        onClose={() => !saveCategoryLoading && setShowCategoryModal(false)}
        title={newCategoryParentId ? 'Nuevo código de producto' : 'Nueva categoría de upselling'}
        subtitle={
          newCategoryParentId
            ? 'Código de producto: exactamente 2 dígitos (ej. 23 = Espátulas). Queda bajo la categoría UP##.'
            : 'Categoría raíz: UP01–UP13 (ej. UP04). Las subcategorías son códigos de producto de 2 dígitos.'
        }
        size="sm"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveCategoryLoading}
        closeOnEscape={!saveCategoryLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Categoría padre (opcional)</span>
            <select
              value={newCategoryParentId}
              onChange={(e) => setNewCategoryParentId(e.target.value)}
              disabled={saveCategoryLoading}
              style={{ ...selectStyle, width: '100%' }}
            >
              <option value="">— Ninguna (categoría principal) —</option>
              {rootCategories.map((r) => (
                <option key={r.id} value={String(r.id)}>
                  {r.code} — {r.name}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Código</span>
            <input
              type="text"
              value={newCatCode}
              onChange={(e) => setNewCatCode(e.target.value.toUpperCase())}
              disabled={saveCategoryLoading}
              placeholder="Ej. BRH"
              maxLength={10}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre</span>
            <input
              type="text"
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              disabled={saveCategoryLoading}
              placeholder="Ej. Brochas y Pinceles"
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          {categoryFormError ? <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{categoryFormError}</p> : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveCategoryLoading}
              onClick={() => setShowCategoryModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveCategoryLoading}
              onClick={handleSaveCategory}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saveCategoryLoading ? 'Guardando…' : 'Guardar categoría'}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showEditCategoryModal}
        onClose={() => !saveEditCategoryLoading && setShowEditCategoryModal(false)}
        title="Editar categoría"
        subtitle="Código y padre: si la categoría tiene subcategorías, no puede asignarse un padre. El código se propaga a productos (A012)."
        size="sm"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveEditCategoryLoading}
        closeOnEscape={!saveEditCategoryLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Categoría padre</span>
            <select
              value={editParentId}
              onChange={(e) => setEditParentId(e.target.value)}
              disabled={saveEditCategoryLoading || editCategoryHasChildren}
              style={{ ...selectStyle, width: '100%' }}
            >
              <option value="">— Raíz (sin padre) —</option>
              {rootCategoriesForParentSelect.map((r) => (
                <option key={r.id} value={String(r.id)}>
                  {r.code} — {r.name}
                </option>
              ))}
            </select>
            {editCategoryHasChildren ? (
              <span style={{ fontSize: '11px', color: muted }}>
                Tiene subcategorías: debe permanecer como categoría principal.
              </span>
            ) : null}
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Código</span>
            <input
              type="text"
              value={editCategoryCode}
              onChange={(e) => setEditCategoryCode(e.target.value.toUpperCase())}
              disabled={saveEditCategoryLoading}
              maxLength={10}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre visible</span>
            <input
              type="text"
              value={editDisplayName}
              onChange={(e) => setEditDisplayName(e.target.value)}
              disabled={saveEditCategoryLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Orden (sort_order)</span>
            <input
              type="number"
              step={1}
              value={editSortOrder}
              onChange={(e) => setEditSortOrder(e.target.value)}
              disabled={saveEditCategoryLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: text }}>
            <input
              type="checkbox"
              checked={editIsActive}
              onChange={(e) => setEditIsActive(e.target.checked)}
              disabled={saveEditCategoryLoading}
            />
            Categoría activa (visible en listas y cotizaciones)
          </label>
          {editCategoryFormError ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{editCategoryFormError}</p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveEditCategoryLoading}
              onClick={() => setShowEditCategoryModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveEditCategoryLoading}
              onClick={handleSaveEditCategory}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saveEditCategoryLoading ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showFormatModal}
        onClose={() => !saveFormatLoading && setShowFormatModal(false)}
        title="Nuevo formato"
        subtitle="Códigos cortos (mayúsculas y números) para usar como formatos posibles en upselling (A016)."
        size="sm"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveFormatLoading}
        closeOnEscape={!saveFormatLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Código</span>
            <input
              type="text"
              value={newFormatCode}
              onChange={(e) => setNewFormatCode(e.target.value.toUpperCase())}
              disabled={saveFormatLoading}
              placeholder="Ej. A4, DIG"
              maxLength={12}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre</span>
            <input
              type="text"
              value={newFormatName}
              onChange={(e) => setNewFormatName(e.target.value)}
              disabled={saveFormatLoading}
              placeholder="Ej. Pliego A4, Entrega digital"
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          {formatFormError ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{formatFormError}</p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveFormatLoading}
              onClick={() => setShowFormatModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveFormatLoading}
              onClick={handleSaveFormat}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saveFormatLoading ? 'Guardando…' : 'Guardar formato'}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showEditFormatModal}
        onClose={() => !saveEditFormatLoading && setShowEditFormatModal(false)}
        title="Editar formato"
        subtitle="Orden y estado se usan al listar formatos en cotizaciones u otros flujos."
        size="sm"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveEditFormatLoading}
        closeOnEscape={!saveEditFormatLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Código</span>
            <input
              type="text"
              value={editFormatCode}
              onChange={(e) => setEditFormatCode(e.target.value.toUpperCase())}
              disabled={saveEditFormatLoading}
              maxLength={12}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre visible</span>
            <input
              type="text"
              value={editFormatName}
              onChange={(e) => setEditFormatName(e.target.value)}
              disabled={saveEditFormatLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Orden (sort_order)</span>
            <input
              type="number"
              step={1}
              value={editFormatSortOrder}
              onChange={(e) => setEditFormatSortOrder(e.target.value)}
              disabled={saveEditFormatLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: text }}>
            <input
              type="checkbox"
              checked={editFormatIsActive}
              onChange={(e) => setEditFormatIsActive(e.target.checked)}
              disabled={saveEditFormatLoading}
            />
            Formato activo
          </label>
          {editFormatFormError ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{editFormatFormError}</p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveEditFormatLoading}
              onClick={() => setShowEditFormatModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveEditFormatLoading}
              onClick={handleSaveEditFormat}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saveEditFormatLoading ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showBrandModal}
        onClose={() => !saveBrandLoading && setShowBrandModal(false)}
        title="Nueva marca"
        subtitle="Código M + 2 caracteres (ej. M26). Usa MXX para «Sin marca»."
        size="sm"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveBrandLoading}
        closeOnEscape={!saveBrandLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Código</span>
            <input
              type="text"
              value={newBrandCode}
              onChange={(e) => setNewBrandCode(e.target.value.toUpperCase())}
              disabled={saveBrandLoading}
              placeholder="MXX"
              maxLength={3}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre</span>
            <input
              type="text"
              value={newBrandName}
              onChange={(e) => setNewBrandName(e.target.value)}
              disabled={saveBrandLoading}
              placeholder="Ej. Artel"
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          {brandFormError ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{brandFormError}</p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveBrandLoading}
              onClick={() => setShowBrandModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveBrandLoading}
              onClick={handleSaveBrand}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saveBrandLoading ? 'Guardando…' : 'Crear marca'}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showEditBrandModal}
        onClose={() => !saveEditBrandLoading && setShowEditBrandModal(false)}
        title="Editar marca"
        subtitle="Actualiza código, nombre, orden o estado."
        size="sm"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveEditBrandLoading}
        closeOnEscape={!saveEditBrandLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Código</span>
            <input
              type="text"
              value={editBrandCode}
              onChange={(e) => setEditBrandCode(e.target.value.toUpperCase())}
              disabled={saveEditBrandLoading}
              maxLength={3}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre visible</span>
            <input
              type="text"
              value={editBrandName}
              onChange={(e) => setEditBrandName(e.target.value)}
              disabled={saveEditBrandLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Orden (sort_order)</span>
            <input
              type="number"
              step={1}
              value={editBrandSortOrder}
              onChange={(e) => setEditBrandSortOrder(e.target.value)}
              disabled={saveEditBrandLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: text }}>
            <input
              type="checkbox"
              checked={editBrandIsActive}
              onChange={(e) => setEditBrandIsActive(e.target.checked)}
              disabled={saveEditBrandLoading}
            />
            Marca activa
          </label>
          {editBrandFormError ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{editBrandFormError}</p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveEditBrandLoading}
              onClick={() => setShowEditBrandModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveEditBrandLoading}
              onClick={handleSaveEditBrand}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saveEditBrandLoading ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default UpsellingTable3;
